/**
 * Clinic Location Detail Page
 *
 * 拠点詳細 - マスタメンテナンス
 * Display detailed information for a specific clinic location (read-only).
 */

import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { TableProps } from 'antd';
import { useMainClinicLocation } from '@/hooks/main/useMainClinicLocation';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import {
  AppBackButton,
  AppBreadcrumb,
  AppTable,
  AppTableActionButton,
  AppSpin,
  AppContainer,
} from '@/components/atoms';
import { CollapsibleCard, InfoRow, TwoColumnInfoRow } from '@/components/molecules';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import { formatJapaneseDate } from '@/utils/dateFormat';
import { NotFound } from '@/pages/main/system/NotFound';
import type { ClinicLocationManager, ClinicLocationMassager } from './types';
import {
  ClinicLocationManagerDetailModal,
  ClinicLocationMassagerDetailModal,
} from '@/components/organisms';
import { renderTableCellTruncate } from '@/utils/tableCellTruncate';

/**
 * Main ClinicLocationDetailPage Component
 */
const ClinicLocationDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant, id } = useParams<{ tenant: string; id: string }>();
  const locationId = id ? parseInt(id, 10) : 0;
  const currentSite = tenant || 'medix';
  const { serviceType } = useTheme();
  const isA2 = serviceType === SERVICE_TYPE_A2;

  // React Query hook
  const { data: response, isLoading, isError } = useMainClinicLocation(locationId);

  // Extract data from response area
  const location = response?.data;

  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);

  const handleOpenManagerModal = (managerId: number) => {
    setSelectedManagerId(managerId);
    setIsManagerModalOpen(true);
  };

  const handleCloseManagerModal = () => {
    setIsManagerModalOpen(false);
    setSelectedManagerId(null);
  };

  const [selectedMassagerId, setSelectedMassagerId] = useState<number | null>(null);
  const [isMassagerModalOpen, setIsMassagerModalOpen] = useState(false);

  const handleOpenMassagerModal = (id: number) => {
    setSelectedMassagerId(id);
    setIsMassagerModalOpen(true);
  };

  const handleCloseMassagerModal = () => {
    setIsMassagerModalOpen(false);
    setSelectedMassagerId(null);
  };

  const managers = useMemo(() => {
    const list = location?.managers || [];
    return [...list].sort((a, b) => (b.available_from || '').localeCompare(a.available_from || ''));
  }, [location?.managers]);

  const massagers = useMemo(() => {
    const list = location?.massagers || [];
    return [...list].sort((a, b) => {
      const sa = Number(a.massager?.serial_number ?? 0);
      const sb = Number(b.massager?.serial_number ?? 0);
      return sa - sb;
    });
  }, [location?.massagers]);

  // Breadcrumb items
  const breadcrumbItems = useMemo(
    () => [
      {
        title: (
          <Link to={`/${currentSite}/clinic_locations/init`} className="!text-inherit">
            拠点一覧
          </Link>
        ),
      },
      { title: `表示:${location?.serial_number || ''}` },
    ],
    [currentSite, location?.serial_number]
  );

  // Manager table columns
  const managerColumns: TableProps<ClinicLocationManager>['columns'] = useMemo(
    () => [
      {
        title: '施術管理者',
        key: 'massager_name',
        width: 800,
        ellipsis: false,
        render: (_, record) =>
          renderTableCellTruncate(record.massager?.name, { width: 800, emptyPlaceholder: '' }),
      },
      {
        title: '有効期限',
        key: 'available_period',
        render: (_, record) => formatPeriodDisplay(record.available_from, record.available_to),
      },
      {
        title: '操作',
        key: 'action',
        align: 'center',
        className: '!w-0 whitespace-nowrap',
        width: 60,
        render: (_, record) => (
          <div className="flex justify-center gap-1 px-2 w-fit">
            <AppTableActionButton
              action="detail"
              onClick={() => handleOpenManagerModal(record.id)}
              title="詳細表示"
            >
              詳細
            </AppTableActionButton>
          </div>
        ),
      },
    ],
    []
  );

  // Massager table columns
  const massagerColumns: TableProps<ClinicLocationMassager>['columns'] = useMemo(
    () => [
      {
        title: isA2 ? 'はり・きゅう師' : 'マッサージ師',
        key: 'massager_name',
        width: 800,
        ellipsis: false,
        render: (_, record) =>
          renderTableCellTruncate(record.massager?.name, { width: 800, emptyPlaceholder: '' }),
      },
      {
        title: '有効期限',
        key: 'available_period',
        render: (_, record) => formatPeriodDisplay(record.available_from, record.available_to),
      },
      {
        title: '操作',
        key: 'action',
        align: 'center',
        className: '!w-0 whitespace-nowrap',
        width: 60,
        render: (_, record) => (
          <div className="flex justify-center gap-1 px-2 w-fit">
            <AppTableActionButton
              action="detail"
              onClick={() => handleOpenMassagerModal(record.id)}
              title="詳細表示"
            >
              詳細
            </AppTableActionButton>
          </div>
        ),
      },
    ],
    [isA2]
  );

  // Check if this is initial loading
  const isInitialLoading = isLoading && !location;

  // Error state - show 404 for not found or any other error when data doesn't exist
  if (isError && !location) {
    return <NotFound />;
  }

  // No data state
  if (!isLoading && !location) {
    return (
      <AppContainer className="pb-24 space-y-4">
        <div className="flex items-center justify-between">
          <AppBreadcrumb
            items={breadcrumbItems}
            separator="/"
            paramsLabel="表示"
            showHome={false}
          />
          <AppBackButton />
        </div>
        <div className="py-8 text-center text-gray-500">データがありません</div>
      </AppContainer>
    );
  }

  return (
    <AppSpin spinning={isInitialLoading}>
      <div className="pb-24 space-y-4">
        <div className="flex items-center justify-between">
          <AppBreadcrumb items={breadcrumbItems} separator="/" showHome={true} />
          <AppBackButton onClick={() => navigate(`/${currentSite}/clinic_locations/init`)} />
        </div>

        <CollapsibleCard title="拠点情報">
          <div className="space-y-1">
            <TwoColumnInfoRow
              label1="拠点名"
              value1={renderTableCellTruncate(location?.location_name, { emptyPlaceholder: '' })}
              label2="拠点名カナ"
              value2={renderTableCellTruncate(location?.location_name_kana, {
                emptyPlaceholder: '',
              })}
            />
            <InfoRow
              label="有効期限"
              value={
                formatPeriodDisplay(location?.available_from, location?.available_to) || undefined
              }
            />
            <InfoRow
              label="住所"
              value={renderTableCellTruncate(
                location?.postal_code1 && location?.postal_code2
                  ? `${location.postal_code1}-${location.postal_code2} ${location.address1 || ''}${location.address2 || ''}${location.address3 || ''}`
                  : `${location?.address1 || ''}${location?.address2 || ''}${location?.address3 || ''}`
              )}
            />
            <TwoColumnInfoRow
              label1="電話番号"
              value1={location?.telephone}
              label2="法人個人"
              value2={
                location?.is_compnay === 1 ? '法人' : location?.is_compnay === 0 ? '個人' : ''
              }
            />
            <TwoColumnInfoRow
              label1="開設者名"
              value1={renderTableCellTruncate(location?.founder_name, { emptyPlaceholder: '' })}
              label2="開設者名カナ"
              value2={renderTableCellTruncate(location?.founder_name_kana, {
                emptyPlaceholder: '',
              })}
            />
            <TwoColumnInfoRow
              label1="開設者住所"
              value1={renderTableCellTruncate(
                !location?.founder_postal_code1 &&
                  !location?.founder_postal_code2 &&
                  !location?.founder_address1 &&
                  !location?.founder_address2 &&
                  !location?.founder_address3
                  ? ''
                  : location?.founder_postal_code1 && location?.founder_postal_code2
                    ? `${location.founder_postal_code1}-${location.founder_postal_code2} ${location.founder_address1 || ''}${location.founder_address2 || ''}${location.founder_address3 || ''}`
                    : `${location?.founder_address1 || ''}${location?.founder_address2 || ''}${location?.founder_address3 || ''}`,
                { emptyPlaceholder: '' }
              )}
              label2="開設者電話番号"
              value2={location?.founder_telephone || ''}
            />
            <InfoRow
              label="開設者生年月日"
              value={
                location?.founder_date_of_birth
                  ? formatJapaneseDate(location.founder_date_of_birth)
                  : ''
              }
            />
          </div>
        </CollapsibleCard>

        {/* 3. 施術管理者 */}
        <CollapsibleCard title="施術管理者">
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-700">該当件数: {managers.length} 件</div>
          </div>

          <AppTable
            columns={managerColumns}
            dataSource={managers}
            rowKey="id"
            pagination={false}
            tableLayout="auto"
          />
        </CollapsibleCard>

        {/* 4. マッサージ師 */}
        <CollapsibleCard title={isA2 ? 'はり・きゅう師' : 'マッサージ師'}>
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-700">該当件数: {massagers.length} 件</div>
          </div>

          <AppTable
            columns={massagerColumns}
            dataSource={massagers}
            rowKey="id"
            pagination={false}
            tableLayout="auto"
          />
        </CollapsibleCard>

        {/* Manager Detail Modal */}
        {selectedManagerId && (
          <ClinicLocationManagerDetailModal
            isOpen={isManagerModalOpen}
            onClose={handleCloseManagerModal}
            managerData={managers.find(m => m.id === selectedManagerId) || null}
            serviceType={serviceType}
          />
        )}

        {/* Massager Detail Modal */}
        {selectedMassagerId && (
          <ClinicLocationMassagerDetailModal
            isOpen={isMassagerModalOpen}
            onClose={handleCloseMassagerModal}
            massagerData={massagers.find(m => m.id === selectedMassagerId) || null}
            serviceType={serviceType}
          />
        )}
      </div>
    </AppSpin>
  );
};

export default ClinicLocationDetailPage;
